import { useEffect, useState } from "react";
import { Save, Mail, Phone, MapPin, User, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader } from "../ui";
import { useAuth } from "../AuthContext";
import { formatPhone } from "../../lib/masks";

export default function AssociateMeusDados() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    phone: "",
    address: "",
    boleto_email: "",
    financial_contact_name: "",
    financial_contact_email: "",
    financial_contact_phone: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        phone: user.phone || "",
        address: user.address || "",
        boleto_email: user.boleto_email || "",
        financial_contact_name: user.financial_contact_name || "",
        financial_contact_email: user.financial_contact_email || "",
        financial_contact_phone: user.financial_contact_phone || "",
      });
    }
  }, [user]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/portal/me/financial", {
        ...form,
        boleto_email: form.boleto_email || null,
        financial_contact_email: form.financial_contact_email || null,
      });
      toast.success("Dados atualizados!");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="associate-meus-dados">
      <PageHeader
        title="Meus Dados de Cobrança"
        subtitle="Mantenha atualizados o endereço, o e-mail de envio de boletos e os contatos do seu departamento financeiro. Todos os recibos e boletos da ABLEMP serão enviados para os e-mails informados aqui."
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-serif-display text-xl mb-4">Contato pessoal</h3>
            <div className="space-y-3">
              <ReadOnly label="Nome" value={user?.name} />
              <ReadOnly label="E-mail principal" value={user?.email} />
              <div>
                <Label>Telefone</Label>
                <Input
                  testid="md-phone"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))
                  }
                  placeholder="(11) 99999-9999"
                  maxLength={15}
                />
              </div>
              <div>
                <Label>Endereço</Label>
                <Input
                  testid="md-address"
                  value={form.address}
                  onChange={upd("address")}
                  placeholder="Rua, número, bairro, cidade — UF, CEP"
                />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail size={18} className="text-[#002776]" />
              <h3 className="font-serif-display text-xl">
                Envio de boletos e recibos
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Boletos, recibos e comunicados financeiros serão enviados para
              estes endereços, além do seu e-mail principal.
            </p>
            <div className="space-y-3">
              <div>
                <Label>E-mail para envio de Boletos</Label>
                <Input
                  testid="md-boleto-email"
                  type="email"
                  value={form.boleto_email}
                  onChange={upd("boleto_email")}
                  placeholder="boletos@empresa.com.br"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Nome do Contato — Financeiro</Label>
                  <Input
                    testid="md-fin-name"
                    value={form.financial_contact_name}
                    onChange={upd("financial_contact_name")}
                    placeholder="Ex.: Maria Souza"
                  />
                </div>
                <div>
                  <Label>Telefone do Financeiro</Label>
                  <Input
                    testid="md-fin-phone"
                    value={form.financial_contact_phone}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        financial_contact_phone: formatPhone(e.target.value),
                      }))
                    }
                    placeholder="(11) 99999-9999"
                    maxLength={15}
                  />
                </div>
                <div>
                  <Label>E-mail do Financeiro</Label>
                  <Input
                    testid="md-fin-email"
                    type="email"
                    value={form.financial_contact_email}
                    onChange={upd("financial_contact_email")}
                    placeholder="financeiro@empresa.com.br"
                  />
                </div>
              </div>
            </div>
          </Card>

          <button
            type="submit"
            disabled={busy}
            data-testid="md-submit"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
          >
            <Save size={14} /> {busy ? "Salvando..." : "Salvar alterações"}
          </button>
        </form>

        <div>
          <Card className="p-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#002776] mb-2">
              Dica
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              Manter um e-mail dedicado para boletos e um contato direto do
              financeiro evita atrasos em pagamentos e garante que sua empresa
              receba os documentos no setor correto.
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Mail size={14} /> Boletos & recibos
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <User size={14} /> Contato financeiro
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Phone size={14} /> Telefone direto
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin size={14} /> Endereço para recibo
              </div>
            </div>
          </Card>

          <Card className="p-5 mt-4 bg-[#0A0F14] text-white">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#FEDF00] mb-2">
              Precisa alterar mais dados?
            </div>
            <p className="text-sm text-white/80 leading-relaxed">
              Para alterar nome, CPF/CNPJ ou tipo de associação, entre em
              contato com a administração da ABLEMP pelo e-mail{" "}
              <strong className="text-white">contato@ablemp.org.br</strong>.
            </p>
          </Card>

          <PasswordCard />
        </div>
      </div>
    </div>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("A nova senha precisa ter ao menos 8 caracteres.");
      return;
    }
    if (next !== confirm) {
      toast.error("A confirmação não confere com a nova senha.");
      return;
    }
    if (current === next) {
      toast.error("A nova senha deve ser diferente da atual.");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/portal/me/password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Senha alterada com sucesso!");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 mt-4" testid="md-password-card">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound size={16} className="text-[#002776]" />
        <h3 className="font-serif-display text-lg">Alterar senha</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Recomendamos senhas com pelo menos 8 caracteres, misturando letras,
        números e símbolos.
      </p>
      <form onSubmit={submit} className="space-y-3" data-testid="md-password-form">
        <div>
          <Label>Senha atual</Label>
          <div className="mt-1 flex items-stretch border border-slate-300 rounded-sm focus-within:ring-2 focus-within:ring-[#002776]/30 focus-within:border-[#002776]">
            <input
              data-testid="md-current-password"
              type={show ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              aria-label={show ? "Ocultar senha" : "Mostrar senha"}
              className="px-3 text-slate-500 hover:text-[#002776]"
            >
              {show ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <div>
          <Label>Nova senha *</Label>
          <Input
            testid="md-new-password"
            type={show ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>
        <div>
          <Label>Confirmar nova senha *</Label>
          <Input
            testid="md-confirm-password"
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>
        <button
          type="submit"
          disabled={busy || !current || !next || !confirm}
          data-testid="md-password-submit"
          className="w-full inline-flex items-center justify-center gap-2 mt-1 px-5 py-2.5 bg-[#002776] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#0A0F14] disabled:opacity-50"
        >
          <KeyRound size={14} /> {busy ? "Alterando..." : "Salvar nova senha"}
        </button>
      </form>
    </Card>
  );
}

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
    {children}
  </label>
);

const Input = ({ testid, ...rest }) => (
  <input
    data-testid={testid}
    {...rest}
    className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002776]/30 focus:border-[#002776]"
  />
);

const ReadOnly = ({ label, value }) => (
  <div>
    <Label>{label}</Label>
    <div className="mt-1 w-full border border-slate-200 bg-slate-50 px-3 py-2.5 rounded-sm text-slate-700 text-sm">
      {value || "—"}
    </div>
  </div>
);
